import { useState } from "react";

interface UploadedProduct {
  id: number;
  name: string;
  price: string;
  category: string;
  stock: number;
  status: "active" | "pending" | "paused";
  sold: number;
  image: string;
}

const mockUploaded: UploadedProduct[] = [
  {
    id: 101,
    name: "Wireless Noise-Cancelling Headphones Pro",
    price: "$34.50",
    category: "Electronics",
    stock: 847,
    status: "active",
    sold: 1243,
    image: "https://public.readdy.ai/ai/img_res/407c927afa2b909ae91d05c8bc7c57b5.jpg",
  },
  {
    id: 102,
    name: "Mini Projector 4K Portable",
    price: "$72.00",
    category: "Electronics",
    stock: 234,
    status: "active",
    sold: 689,
    image: "https://readdy.ai/api/search-image?query=mini%20projector%204K%20portable%20home%20cinema%20product%20white%20clean%20background%20studio&width=80&height=80&seq=sdash2&orientation=squarish",
  },
  {
    id: 103,
    name: "Foldable Laptop Stand Aluminum",
    price: "$16.50",
    category: "Office",
    stock: 543,
    status: "pending",
    sold: 0,
    image: "https://readdy.ai/api/search-image?query=foldable%20laptop%20stand%20aluminum%20silver%20product%20white%20clean%20background%20studio%20office&width=80&height=80&seq=sdash3&orientation=squarish",
  },
];

const categories = ["Electronics", "Kitchen", "Photography", "Wearables", "Furniture", "Sports", "Office", "Fitness", "Home"];

interface Props {
  supplierName: string;
  onClose: () => void;
}

export default function SupplierDashboard({ supplierName, onClose }: Props) {
  const [activeTab, setActiveTab] = useState<"products" | "upload" | "orders">("products");
  const [products, setProducts] = useState<UploadedProduct[]>(mockUploaded);
  const [uploadForm, setUploadForm] = useState({
    name: "",
    price: "",
    originalPrice: "",
    category: "",
    stock: "",
    description: "",
    shipping: "",
    commission: "10",
  });
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setUploadForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => setImagePreview(ev.target?.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = (e: React.FormEvent) => {
    e.preventDefault();
    setUploading(true);
    setTimeout(() => {
      const newProduct: UploadedProduct = {
        id: Date.now(),
        name: uploadForm.name,
        price: `$${uploadForm.price}`,
        category: uploadForm.category,
        stock: parseInt(uploadForm.stock) || 0,
        status: "pending",
        sold: 0,
        image: imagePreview || "https://readdy.ai/api/search-image?query=product%20placeholder%20clean%20white%20background%20minimal&width=80&height=80&seq=newprod&orientation=squarish",
      };
      setProducts((prev) => [newProduct, ...prev]);
      setUploadForm({ name: "", price: "", originalPrice: "", category: "", stock: "", description: "", shipping: "", commission: "10" });
      setImagePreview(null);
      setUploading(false);
      setUploadSuccess(true);
      setTimeout(() => setUploadSuccess(false), 3000);
      setActiveTab("products");
    }, 1200);
  };

  const statusColors: Record<string, string> = {
    active: "bg-green-100 text-green-700",
    pending: "bg-amber-100 text-amber-700",
    paused: "bg-stone-100 text-stone-500",
  };

  const totalSold = products.reduce((a, p) => a + p.sold, 0);
  const totalRevenue = products.reduce((a, p) => a + p.sold * parseFloat(p.price.replace("$", "")), 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col shadow-xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-500 to-teal-500 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-white font-bold text-lg">Supplier Dashboard</h2>
            <p className="text-white/80 text-sm">{supplierName}</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-0 border-b border-stone-100 flex-shrink-0">
          {[
            { label: "Products Listed", value: products.length, icon: "ri-box-3-line", color: "text-emerald-500" },
            { label: "Total Sold", value: totalSold.toLocaleString(), icon: "ri-shopping-cart-2-line", color: "text-amber-500" },
            { label: "Revenue", value: `$${totalRevenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`, icon: "ri-money-dollar-circle-line", color: "text-sky-500" },
          ].map((s) => (
            <div key={s.label} className="flex flex-col items-center py-4 border-r border-stone-100 last:border-r-0">
              <div className={`w-8 h-8 flex items-center justify-center mb-1`}>
                <i className={`${s.icon} ${s.color} text-xl`}></i>
              </div>
              <div className="font-bold text-gray-900 text-lg">{s.value}</div>
              <div className="text-stone-400 text-xs">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex border-b border-stone-100 flex-shrink-0">
          {[
            { key: "products", label: "My Products", icon: "ri-box-3-line" },
            { key: "upload", label: "Upload Product", icon: "ri-upload-cloud-2-line" },
            { key: "orders", label: "Orders", icon: "ri-file-list-3-line" },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as "products" | "upload" | "orders")}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === tab.key
                  ? "border-emerald-500 text-emerald-600"
                  : "border-transparent text-stone-500 hover:text-gray-700"
              }`}
            >
              <i className={tab.icon}></i>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Products Tab */}
          {activeTab === "products" && (
            <div className="space-y-3">
              {uploadSuccess && (
                <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 rounded-xl px-4 py-3 text-sm font-medium">
                  <i className="ri-checkbox-circle-line text-lg"></i>
                  Product uploaded successfully! It&apos;s under review.
                </div>
              )}
              {products.map((p) => (
                <div key={p.id} className="flex items-center gap-4 bg-stone-50 rounded-xl p-4 border border-stone-100">
                  <div className="w-14 h-14 flex items-center justify-center rounded-lg overflow-hidden flex-shrink-0">
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-900 text-sm truncate">{p.name}</div>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-emerald-600 font-bold text-sm">{p.price}</span>
                      <span className="text-stone-400 text-xs">{p.category}</span>
                      <span className="text-stone-400 text-xs">Stock: {p.stock}</span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${statusColors[p.status]}`}>
                      {p.status.charAt(0).toUpperCase() + p.status.slice(1)}
                    </span>
                    <span className="text-stone-400 text-xs">{p.sold} sold</span>
                  </div>
                </div>
              ))}
              <button
                onClick={() => setActiveTab("upload")}
                className="w-full border-2 border-dashed border-emerald-200 text-emerald-600 hover:bg-emerald-50 rounded-xl py-4 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-add-line"></i>
                Upload New Product
              </button>
            </div>
          )}

          {/* Upload Tab */}
          {activeTab === "upload" && (
            <form onSubmit={handleUpload} className="space-y-4">
              <h3 className="font-bold text-gray-900 text-base mb-4">Upload New Product</h3>

              {/* Image Upload */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-2">Product Photos</label>
                <label className="flex flex-col items-center justify-center w-full h-36 border-2 border-dashed border-stone-200 rounded-xl cursor-pointer hover:border-emerald-300 hover:bg-emerald-50 transition-colors overflow-hidden">
                  {imagePreview ? (
                    <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <div className="flex flex-col items-center gap-2 text-stone-400">
                      <div className="w-10 h-10 flex items-center justify-center">
                        <i className="ri-image-add-line text-3xl"></i>
                      </div>
                      <span className="text-sm font-medium">Click to upload product photos</span>
                      <span className="text-xs">PNG, JPG up to 10MB</span>
                    </div>
                  )}
                  <input type="file" accept="image/*" className="hidden" onChange={handleImageChange} />
                </label>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Product Name *</label>
                  <input
                    name="name"
                    value={uploadForm.name}
                    onChange={handleFormChange}
                    required
                    placeholder="e.g. Wireless Bluetooth Speaker"
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Sale Price (USD) *</label>
                  <input
                    name="price"
                    value={uploadForm.price}
                    onChange={handleFormChange}
                    required
                    placeholder="e.g. 29.99"
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Original Price (USD)</label>
                  <input
                    name="originalPrice"
                    value={uploadForm.originalPrice}
                    onChange={handleFormChange}
                    placeholder="e.g. 79.99"
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Category *</label>
                  <select
                    name="category"
                    value={uploadForm.category}
                    onChange={handleFormChange}
                    required
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-400 transition-colors bg-white cursor-pointer"
                  >
                    <option value="">Select category</option>
                    {categories.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Stock Quantity *</label>
                  <input
                    name="stock"
                    value={uploadForm.stock}
                    onChange={handleFormChange}
                    required
                    type="number"
                    min="1"
                    placeholder="e.g. 500"
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Shipping Time</label>
                  <input
                    name="shipping"
                    value={uploadForm.shipping}
                    onChange={handleFormChange}
                    placeholder="e.g. 5-8 days"
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Influencer Commission %</label>
                  <select
                    name="commission"
                    value={uploadForm.commission}
                    onChange={handleFormChange}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-400 transition-colors bg-white cursor-pointer"
                  >
                    <option value="8">8% Commission</option>
                    <option value="10">10% Commission</option>
                    <option value="12">12% Commission</option>
                    <option value="15">15% Commission</option>
                    <option value="20">20% Commission</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Product Description *</label>
                  <textarea
                    name="description"
                    value={uploadForm.description}
                    onChange={handleFormChange}
                    required
                    maxLength={500}
                    rows={4}
                    placeholder="Describe your product in detail — features, materials, dimensions, use cases..."
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-400 transition-colors resize-none"
                  />
                  <div className="text-right text-xs text-stone-400 mt-0.5">{uploadForm.description.length}/500</div>
                </div>
              </div>

              <button
                type="submit"
                disabled={uploading}
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-3 rounded-xl transition-colors cursor-pointer whitespace-nowrap text-sm disabled:opacity-60"
              >
                {uploading ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Uploading Product...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-upload-cloud-2-line"></i>
                    Submit Product for Review
                  </span>
                )}
              </button>
            </form>
          )}

          {/* Orders Tab */}
          {activeTab === "orders" && (
            <div className="space-y-3">
              <h3 className="font-bold text-gray-900 text-base mb-4">Recent Orders</h3>
              {[
                { id: "#ORD-8821", product: "Wireless Noise-Cancelling Headphones Pro", buyer: "Sarah M.", qty: 2, total: "$69.00", status: "Shipped", date: "Apr 9, 2026" },
                { id: "#ORD-8819", product: "Mini Projector 4K Portable", buyer: "James T.", qty: 1, total: "$72.00", status: "Processing", date: "Apr 9, 2026" },
                { id: "#ORD-8815", product: "Wireless Noise-Cancelling Headphones Pro", buyer: "Liu W.", qty: 3, total: "$103.50", status: "Delivered", date: "Apr 8, 2026" },
                { id: "#ORD-8810", product: "Foldable Laptop Stand Aluminum", buyer: "Priya K.", qty: 1, total: "$16.50", status: "Delivered", date: "Apr 7, 2026" },
                { id: "#ORD-8804", product: "Mini Projector 4K Portable", buyer: "Carlos R.", qty: 2, total: "$144.00", status: "Delivered", date: "Apr 6, 2026" },
              ].map((order) => (
                <div key={order.id} className="bg-stone-50 rounded-xl p-4 border border-stone-100">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-gray-900 text-sm">{order.id}</span>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                          order.status === "Delivered" ? "bg-green-100 text-green-700" :
                          order.status === "Shipped" ? "bg-sky-100 text-sky-700" :
                          "bg-amber-100 text-amber-700"
                        }`}>{order.status}</span>
                      </div>
                      <div className="text-stone-600 text-xs truncate">{order.product}</div>
                      <div className="text-stone-400 text-xs mt-1">Buyer: {order.buyer} · Qty: {order.qty} · {order.date}</div>
                    </div>
                    <div className="text-emerald-600 font-bold text-sm flex-shrink-0">{order.total}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
